import { Inject, Injectable, Optional } from '@angular/core';
import { getProfileBillingAddress, State } from '@app/app.reducer';
import {
  CreateUserProfileRequest,
  CreateUserProfileResponse,
  GetUserProfileResponse
} from '@core/data-access/models/api/user-profile.model';
import { mapUserProfileResponseToUserProfile } from '@core/utils/data-mappers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppConstants } from '@shared/app-constants';
import { USER_DEBOUNCE_TIME } from '@shared/tokens';
import { QuestionnaireDataSection } from '@store/customization/customization.interfaces';
import { CustomizationService } from '@store/customization/customization.service';
import { transformEventsEmailUnsubscriptions } from '@store/customization/forms/newsletter-unsubscriptions-data';
import { SetApiCallResult } from '@store/helpers/helper.actions';
import { HelperService } from '@store/helpers/helper.service';
import {
  ActionTypes,
  ChangeEmail,
  ChangePassword,
  CreateProfile,
  DeleteProfileBillingAddress,
  DownloadTicketById,
  EmailChanged,
  EmailVerified,
  GetEventsEmailUnsubscriptions,
  GetProfile,
  GetProfileBillingAddress,
  GetRegistrationQuestionnaire,
  LogIn,
  PasswordChanged,
  PatchEmail,
  PatchPassword,
  PostRegistrationQuestionnaire,
  ProfileLoading,
  PutProfileBillingAddress,
  RequestPasswordRecovery,
  RequestPasswordRecoveryResult,
  ResetPassword,
  SetEventsEmailUnsubscriptions,
  SetProfile,
  SetProfileBillingAddress,
  SetRegistrationQuestionnaire,
  SetUpdatedUserProfile,
  SetUser,
  UpdateProfile,
  UpdateProfileBillingAddress,
  VerifyEmail
} from '@store/user/user.actions';
import {
  BillingAddressModel,
  EventsEmailUnsubscribeModel,
  QuestionnaireSaveModel,
  UserModel
} from '@store/user/user.interface';
import { UserService } from '@store/user/user.service';
import { omit } from 'lodash';
import { catchError, debounceTime, EMPTY, map, of, switchMap } from 'rxjs';

@Injectable()
export class UserEffect {
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LogIn>(ActionTypes.LOGIN),
      switchMap((data: any) => {
        const { email, password } = data.payload;
        // TODO update interface LoginModel
        if (!email && !password) {
          return EMPTY;
        }

        return this.userService.login(email, password).pipe(
          map((user: UserModel) => {
            user.userType = AppConstants.getUserTypeFromJwt(user);

            if (Object.hasOwn(user, 'id')) {
              this.store.dispatch(new GetProfile(user.id));
            }

            return new SetUser(user);
          }),
          catchError(() => of(new SetUser(null)))
        );
      })
    )
  );

  registrationQuestionnaire$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetRegistrationQuestionnaire>(ActionTypes.GET_REGISTRATION_QUESTIONNAIRE),
      debounceTime(this.userDebounceTime),
      switchMap((data: any) => {
        const eventId = data.payload;
        return this.userService.getRegistrationQuestionnaire(eventId).pipe(
          map((registrationQuestionnaire: QuestionnaireDataSection[]) => {
            const questionnaire: QuestionnaireDataSection[] = registrationQuestionnaire;
            const questionnaireInputs = this.customizationService.transformQuestionnaireIntoInput(questionnaire);

            return new SetRegistrationQuestionnaire(questionnaireInputs);
          }),
          catchError(() => of(new SetRegistrationQuestionnaire([])))
        );
      })
    )
  );

  eventsEmailUnsubscriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetEventsEmailUnsubscriptions>(ActionTypes.GET_EVENTS_EMAIL_UNSUBSCRIPTIONS),
      debounceTime(this.userDebounceTime),
      switchMap((data: any) => {
        const userId = data.payload;

        if (!userId) {
          return EMPTY;
        }

        return this.userService.getEmailUnsubscriptions().pipe(
          map((emailUnsubscriptions: EventsEmailUnsubscribeModel[]) => {
            const eventsEmailUnsubscriptions: EventsEmailUnsubscribeModel[] = emailUnsubscriptions;
            const unsubscriptionInputs = transformEventsEmailUnsubscriptions(eventsEmailUnsubscriptions);

            return new SetEventsEmailUnsubscriptions(unsubscriptionInputs);
          }),
          catchError(() => of(new SetEventsEmailUnsubscriptions([])))
        );
      })
    )
  );

  getProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetProfile>(ActionTypes.GET_PROFILE),
      debounceTime(this.userDebounceTime),
      switchMap((data: any) => {
        const userId = data.payload;

        this.store.dispatch(new ProfileLoading(true));

        if (!userId) {
          this.store.dispatch(new ProfileLoading(false));

          return EMPTY;
        }

        return this.userService.getProfile().pipe(
          map(
            (userProfileResponse: GetUserProfileResponse) =>
              new SetProfile(mapUserProfileResponseToUserProfile(userProfileResponse))
          ),
          catchError(() => of(new SetProfile(null)))
        );
      })
    )
  );

  createProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateProfile>(ActionTypes.POST_PROFILE),
      debounceTime(this.userDebounceTime),
      switchMap((createProfile: CreateProfile) => {
        const { payload: user } = createProfile;

        if (!user) {
          return EMPTY;
        }

        const createUserProfileRequest: CreateUserProfileRequest = omit(user, ['questionnaire']);

        return this.userService.createProfile(createUserProfileRequest).pipe(
          map((createUserProfileResponse: CreateUserProfileResponse) => {
            const { id, authToken, userType } = createUserProfileResponse;

            const questionnaire: QuestionnaireSaveModel = user?.questionnaire;

            if (questionnaire) {
              const questionnaireToSave = {
                userId: createUserProfileResponse.id,
                questionnaire
              };

              setTimeout(() => {
                this.store.dispatch(new PostRegistrationQuestionnaire(questionnaireToSave));
              }, 0);
            }

            this.store.dispatch(
              new SetUser({
                id,
                authToken,
                userType
              })
            );

            this.store.dispatch(new GetProfile(id));
            this.helperService.redirectAfterLogin();

            const userProfile = mapUserProfileResponseToUserProfile(createUserProfileResponse);

            return new SetProfile(userProfile);
          }),
          catchError(() => of(new SetProfile(null)))
        );
      })
    )
  );

  updateProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateProfile>(ActionTypes.PUT_PROFILE),
      debounceTime(this.userDebounceTime),
      switchMap((updateProfile) => {
        const { payload: user } = updateProfile;

        if (!user) {
          return EMPTY;
        }

        return this.userService.updateProfile(user).pipe(
          map((updateUserProfileResponse) =>
            SetUpdatedUserProfile({ profile: mapUserProfileResponseToUserProfile(updateUserProfileResponse) })
          ),
          catchError(() => of(SetUpdatedUserProfile({ profile: null })))
        );
      })
    )
  );

  postRegistrationQuestionnaire$ = createEffect(() =>
    this.actions$.pipe(
      ofType<PostRegistrationQuestionnaire>(ActionTypes.POST_REGISTRATION_QUESTIONNAIRE_PROFILE),
      debounceTime(this.userDebounceTime),
      switchMap((data: any) => {
        if (!data.payload) {
          return EMPTY;
        }

        const { questionnaire } = data.payload;

        return this.userService.saveRegistrationQuestionnaire(questionnaire).pipe(
          map((code: number) => {
            this.helperService.redirectAfterLogin();
            return new SetApiCallResult({
              code,
              message: 'post registration questionnaire success'
            });
          }),
          catchError(() =>
            of(
              new SetApiCallResult({
                code: 0,
                message: 'post registration questionnaire fail'
              })
            )
          )
        );
      })
    )
  );

  getProfileBillingAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetProfileBillingAddress>(ActionTypes.GET_PROFILE_BILLING_ADDRESS),
      debounceTime(this.userDebounceTime),
      switchMap((data: any) => {
        const userId = data.payload;

        if (!userId) {
          return EMPTY;
        }

        return this.userService.getProfileBillingAddress().pipe(
          map((billingAddress: BillingAddressModel[]) => new SetProfileBillingAddress(billingAddress)),
          catchError(() => of(new SetProfileBillingAddress([])))
        );
      })
    )
  );

  putProfileBillingAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType<PutProfileBillingAddress>(ActionTypes.PUT_PROFILE_BILLING_ADDRESS),
      debounceTime(this.userDebounceTime),
      switchMap((actionData: any) => {
        const { userId, addressId, data } = actionData.payload;
        if (!actionData.payload) {
          return EMPTY;
        }

        return this.userService.putProfileBillingAddress(addressId, data).pipe(
          map((billingAddress: BillingAddressModel) => {
            return UpdateProfileBillingAddress(billingAddress);
          }),
          catchError(() => of(UpdateProfileBillingAddress(null)))
        );
      })
    )
  );

  deleteProfileBillingAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteProfileBillingAddress>(ActionTypes.DELETE_PROFILE_BILLING_ADDRESS),
      debounceTime(this.userDebounceTime),
      switchMap((data: any) => {
        const { addressId } = data.payload;

        return this.userService.deleteProfileBillingAddress(addressId).pipe(
          map((deletedBillingAddress: BillingAddressModel) => {
            /* As the billing address to be removed is for sure in the list,
           the next call to redux is actually synchronous */

            let billingAddresses: BillingAddressModel[];
            this.store.select(getProfileBillingAddress).subscribe((addresses) => {
              billingAddresses = addresses;
            });

            const clearedAddresses = billingAddresses.filter((currentAddress: BillingAddressModel) => {
              return currentAddress.id !== deletedBillingAddress.id;
            });

            const addressesClone = JSON.parse(JSON.stringify(clearedAddresses));

            return new SetProfileBillingAddress(addressesClone);
          }),
          catchError(() => of(new SetProfileBillingAddress([])))
        );
      })
    )
  );

  patchEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType<PatchEmail>(ActionTypes.PATCH_EMAIL),
      debounceTime(this.userDebounceTime),
      switchMap((data: any) => {
        const { email, password, language } = data.payload;

        if (!data.payload) {
          return EMPTY;
        }

        return this.userService.setNewEmail(email, password, language).pipe(
          map((code: number) => new EmailChanged(200)),
          catchError(() => {
            return of(new EmailChanged(500));
          })
        );
      })
    )
  );

  patchPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType<PatchPassword>(ActionTypes.PATCH_PASSWORD),
      debounceTime(this.userDebounceTime),
      switchMap((data: any) => {
        if (!data.payload) {
          return EMPTY;
        }

        const { password, language } = data.payload;

        return this.userService.setNewPassword(password, language).pipe(
          map((code: number) => new PasswordChanged(code)),
          catchError(() => of(new PasswordChanged(500)))
        );
      })
    )
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ResetPassword>(ActionTypes.RESET_PASSWORD),
      debounceTime(this.userDebounceTime),
      switchMap((data: any) => {
        if (!data.payload) {
          return EMPTY;
        }

        const { hash, password, crmHash } = data.payload;

        return this.userService.resetPassword(password, hash, crmHash).pipe(
          map((code: any) => {
            return new PasswordChanged(code.status);
          }),
          catchError((err) => {
            console.log('error', err);
            return of(new PasswordChanged(err.status));
          })
        );
      })
    )
  );

  verifyEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType<VerifyEmail>(ActionTypes.VERIFY_EMAIL),
      debounceTime(this.userDebounceTime),
      switchMap((payloadData: any) => {
        if (!payloadData) {
          return EMPTY;
        }

        const { hash, crmHash } = payloadData.payload;

        return this.userService.verifyEmail(hash, crmHash).pipe(
          map((response: any) => {
            return new EmailVerified({
              statusCode: response.status,
              body: {
                assignedTickets: response.body.assignedTickets,
                assignedOrders: response.body.assignedOrders
              }
            });
          }),
          catchError((err) => {
            console.log('error', err);
            return of(
              new EmailVerified({
                statusCode: err.status,
                body: {
                  assignedTickets: null,
                  assignedOrders: null
                }
              })
            );
          })
        );
      })
    )
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ChangePassword>(ActionTypes.CHANGE_PASSWORD),
      debounceTime(this.userDebounceTime),
      switchMap((payloadData: any) => {
        if (!payloadData) {
          return EMPTY;
        }

        const { hash, crmHash } = payloadData.payload;

        return this.userService.changePassword(hash, crmHash).pipe(
          map((code: any) => {
            return new PasswordChanged(code.status);
          }),
          catchError((err) => {
            console.log('error', err);
            return of(new PasswordChanged(err.status));
          })
        );
      })
    )
  );

  changeEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ChangeEmail>(ActionTypes.CHANGE_EMAIL),
      debounceTime(this.userDebounceTime),
      switchMap((payloadData: any) => {
        if (!payloadData) {
          return EMPTY;
        }

        const { hash, crmHash } = payloadData.payload;

        return this.userService.changeEmail(hash, crmHash).pipe(
          map((code: any) => {
            return new EmailChanged(code.status);
          }),
          catchError((err) => {
            console.log('error', err);
            return of(new EmailChanged(err.status));
          })
        );
      })
    )
  );

  requestPasswordRecovery$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RequestPasswordRecovery>(ActionTypes.POST_PASSWORD_RECOVERY),
      debounceTime(this.userDebounceTime),
      switchMap((data: any) => {
        if (!data.payload) {
          return EMPTY;
        }

        return this.userService.forgottenPassword(data.payload).pipe(
          map(() => {
            return new RequestPasswordRecoveryResult(true);
          }),
          catchError(() => of(new RequestPasswordRecoveryResult(false)))
        );
      })
    )
  );

  DownloadTicketById$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DownloadTicketById>(ActionTypes.DOWNLOAD_TICKET_BY_ID),
      debounceTime(this.userDebounceTime),
      switchMap((data: any) => {
        const { id, type, ticketId, packageIndex } = data.payload;

        if (!id) {
          return EMPTY;
        }

        if (type === 'normalTicket') {
          return this.userService.downloadTicketById(id).pipe(
            map((blob: any) => {
              this.helperService.saveToFileSystem(blob, `ticket_${id}.pdf`);

              // if we load the user
              return new SetApiCallResult({
                code: 200,
                message: 'ticket download successfull'
              });
            }),
            catchError(() => {
              return of(
                new SetApiCallResult({
                  code: 0,
                  message: 'ticket download fail'
                })
              );
            })
          );
        }

        if (type === 'invoiceTicket') {
          return this.userService.downloadInvoice(id).pipe(
            map((blob: any) => {
              this.helperService.saveToFileSystem(blob, `invoice_${id}.pdf`);

              // if we load the user
              return new SetApiCallResult({
                code: 200,
                message: 'ticket download successfull'
              });
            }),
            catchError(() => {
              return of(
                new SetApiCallResult({
                  code: 0,
                  message: 'ticket download fail'
                })
              );
            })
          );
        }

        if (type === 'passBook') {
          return this.userService.downloadPassbook(id).pipe(
            map((blob: any) => {
              this.helperService.saveToFileSystem(blob, `passbook_${id}.pkpass`);

              // if we load the user
              return new SetApiCallResult({
                code: 200,
                message: 'ticket download successfull'
              });
            }),
            catchError(() => {
              return of(
                new SetApiCallResult({
                  code: 0,
                  message: 'ticket download fail'
                })
              );
            })
          );
        }

        if (type === 'refundReceipt') {
          return this.userService.downloadRefundReceipt(id).pipe(
            map((blob: any) => {
              this.helperService.saveToFileSystem(blob, `refund_order_${id}.pdf`);

              // if we load the user
              return new SetApiCallResult({
                code: 200,
                message: 'Refund receipt download successfull'
              });
            }),
            catchError(() => {
              return of(
                new SetApiCallResult({
                  code: 0,
                  message: 'Refund receipt download fail'
                })
              );
            })
          );
        }

        if (type === 'refundSingleTicketReceipt') {
          if (ticketId) {
            return this.userService.downloadTicketRefundReceipt(id, ticketId).pipe(
              map((blob: any) => {
                this.helperService.saveToFileSystem(blob, `refund_ticket_${ticketId}.pdf`);

                // if we load the user
                return new SetApiCallResult({
                  code: 200,
                  message: 'Refund receipt download successfull'
                });
              }),
              catchError(() => {
                return of(
                  new SetApiCallResult({
                    code: 0,
                    message: 'Refund receipt download fail'
                  })
                );
              })
            );
          }
        }

        if (type === 'refundPackageReceipt') {
          if (packageIndex > -1) {
            return this.userService.downloadPackageRefundReceipt(id, packageIndex).pipe(
              map((blob: any) => {
                this.helperService.saveToFileSystem(blob, `refund_package_${id}_${packageIndex}.pdf`);

                // if we load the user
                return new SetApiCallResult({
                  code: 200,
                  message: 'Refund receipt download successfull'
                });
              }),
              catchError(() => {
                return of(
                  new SetApiCallResult({
                    code: 0,
                    message: 'Refund receipt download fail'
                  })
                );
              })
            );
          }
        }

        if (type === 'parkingTicket') {
          return this.userService.downloadParkingTicketById(id).pipe(
            map((blob: any) => {
              this.helperService.saveToFileSystem(blob, `parkingTicket_${id}.pdf`);

              // if we load the user
              return new SetApiCallResult({
                code: 200,
                message: 'ticket download successfull'
              });
            }),
            catchError(() => {
              return of(
                new SetApiCallResult({
                  code: 0,
                  message: 'ticket download fail'
                })
              );
            })
          );
        }

        if (type === 'parkingRefundReceipt') {
          if (ticketId) {
            return this.userService.downloadParkingRefundReceipt(id, ticketId).pipe(
              map((blob: any) => {
                this.helperService.saveToFileSystem(blob, `refund_parking_ticket_${ticketId}.pdf`);

                // if we load the user
                return new SetApiCallResult({
                  code: 200,
                  message: 'Refund receipt download successfull'
                });
              }),
              catchError(() => {
                return of(
                  new SetApiCallResult({
                    code: 0,
                    message: 'Refund receipt download fail'
                  })
                );
              })
            );
          }
        }
      })
    )
  );

  constructor(
    @Optional() @Inject(USER_DEBOUNCE_TIME) private userDebounceTime: number,
    private actions$: Actions,
    private userService: UserService,
    private customizationService: CustomizationService,
    private helperService: HelperService,
    private store: Store<State>
  ) {}
}
